import { useState, useEffect } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Poll Activity Component
 * Clean UI design with letter-based options and circular timer
 */

const PollActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onNavigateToWorkbooks,
  onBackToList,
  showResults = false,
  userResponse = null,
  results = null, // Real results data from API/WebSocket
  isActivityCompleted = false, // True if activity was closed by trainer
  isActivityEnded = false, // True if activity has ended (for responded state detection)
}) => {
  // Handle different response formats: { single: 0 }, { value: 0 }, or { type: 'single', value: 0 }
  const getInitialSelection = () => {
    if (!userResponse) return { single: null, multiple: [] };

    // Format: { type: 'single', value: 0 } or { type: 'multiple', value: [0, 1] }
    if (userResponse.type === 'single') {
      return { single: userResponse.value, multiple: [] };
    }
    if (userResponse.type === 'multiple') {
      return { single: null, multiple: userResponse.value || [] };
    }
    // Format: { single: 0 } or { multiple: [0, 1] }
    if (userResponse.single !== undefined) {
      return { single: userResponse.single, multiple: [] };
    }
    if (userResponse.multiple !== undefined) {
      return { single: null, multiple: userResponse.multiple };
    }
    // Format: { value: 0 } (assume single)
    if (userResponse.value !== undefined) {
      return { single: userResponse.value, multiple: [] };
    }
    // Format: { selectedOption: 0 }
    if (userResponse.selectedOption !== undefined) {
      return { single: userResponse.selectedOption, multiple: [] };
    }
    return { single: null, multiple: [] };
  };

  const initialSelection = getInitialSelection();
  const [selectedOption, setSelectedOption] = useState(initialSelection.single);
  const [selectedOptions, setSelectedOptions] = useState(initialSelection.multiple);

  // Sync state when userResponse changes (e.g., after submit)
  useEffect(() => {
    const newSelection = getInitialSelection();
    if (newSelection.single !== null && newSelection.single !== selectedOption) {
      setSelectedOption(newSelection.single);
    }
    if (newSelection.multiple.length > 0 && JSON.stringify(newSelection.multiple) !== JSON.stringify(selectedOptions)) {
      setSelectedOptions(newSelection.multiple);
    }
  }, [userResponse]);

  const isPollMultiple = activity.config?.pollType === 'multiple';
  const options = activity.config?.options || [];

  // Letter labels for options
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

  const toggleMultipleOption = (optionIndex) => {
    if (selectedOptions.includes(optionIndex)) {
      setSelectedOptions(selectedOptions.filter((o) => o !== optionIndex));
    } else {
      setSelectedOptions([...selectedOptions, optionIndex]);
    }
  };

  const handleSubmit = () => {
    // Include the selected option label(s) for better display in reports
    if (isPollMultiple) {
      const selectedLabels = selectedOptions.map(idx => {
        const opt = options[idx];
        return typeof opt === 'string' ? opt : (opt?.text || opt?.label || `Option ${idx + 1}`);
      });
      onSubmit({
        type: 'multiple',
        value: selectedOptions,
        selectedLabels: selectedLabels,
        selectedLabel: selectedLabels.join(', ')
      });
    } else {
      const selectedOpt = options[selectedOption];
      const label = typeof selectedOpt === 'string' ? selectedOpt : (selectedOpt?.text || selectedOpt?.label || `Option ${selectedOption + 1}`);
      onSubmit({
        type: 'single',
        value: selectedOption,
        selectedLabel: label
      });
    }
  };

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress - use actual timeLimit from activity
  const maxTime = activity?.timerEnabled === true
    ? (activity.timeLimit || activity.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;
  const timerProgress = timerEnabled ? ((maxTime - timeRemaining) / maxTime) * 100 : 0;

  // Calculate results from real data or use provided results
  const calculateResultsData = () => {
    const optionsCount = options.length || 4;
    const totalParticipants = activity.totalParticipants || activity.participantCount || activity.responseCount || 1;

    // Get user's selected option from multiple sources
    let userSelectedOption = null;
    let userSelectedOptions = [];

    // Check for single choice selection
    if (selectedOption !== null && selectedOption !== undefined) {
      userSelectedOption = typeof selectedOption === 'string' ? parseInt(selectedOption, 10) : selectedOption;
    }
    if (userSelectedOption === null && userResponse) {
      if (userResponse.type === 'single' && userResponse.value !== undefined) {
        userSelectedOption = typeof userResponse.value === 'string' ? parseInt(userResponse.value, 10) : userResponse.value;
      } else if (userResponse.value !== undefined && userResponse.type !== 'multiple') {
        userSelectedOption = typeof userResponse.value === 'string' ? parseInt(userResponse.value, 10) : userResponse.value;
      } else if (userResponse.selectedOption !== undefined) {
        userSelectedOption = typeof userResponse.selectedOption === 'string' ? parseInt(userResponse.selectedOption, 10) : userResponse.selectedOption;
      } else if (userResponse.single !== undefined) {
        userSelectedOption = typeof userResponse.single === 'string' ? parseInt(userResponse.single, 10) : userResponse.single;
      }
    }
    if (userSelectedOption === null && initialSelection.single !== null && initialSelection.single !== undefined) {
      userSelectedOption = typeof initialSelection.single === 'string' ? parseInt(initialSelection.single, 10) : initialSelection.single;
    }

    // Check for multiple choice selection
    if (selectedOptions.length > 0) {
      userSelectedOptions = selectedOptions;
    } else if (userResponse?.type === 'multiple' && Array.isArray(userResponse.value)) {
      userSelectedOptions = userResponse.value;
    } else if (initialSelection.multiple.length > 0) {
      userSelectedOptions = initialSelection.multiple;
    }

    // Build votes array - start with server results if available, otherwise zeros
    const serverVotes = results?.votes && results.votes.length === optionsCount
      ? [...results.votes]
      : new Array(optionsCount).fill(0);

    // Ensure user's vote is counted in the votes array
    const userHasVoted = isPollMultiple ? userSelectedOptions.length > 0 : userSelectedOption !== null;

    if (userHasVoted) {
      if (isPollMultiple) {
        userSelectedOptions.forEach(idx => {
          const parsedIdx = typeof idx === 'string' ? parseInt(idx, 10) : idx;
          if (typeof parsedIdx === 'number' && !isNaN(parsedIdx) && parsedIdx >= 0 && parsedIdx < serverVotes.length) {
            // Ensure at least 1 vote for user's choice
            if (serverVotes[parsedIdx] === 0) {
              serverVotes[parsedIdx] = 1;
            }
          }
        });
      } else if (typeof userSelectedOption === 'number' && !isNaN(userSelectedOption) && userSelectedOption >= 0 && userSelectedOption < serverVotes.length) {
        // Ensure at least 1 vote for user's choice
        if (serverVotes[userSelectedOption] === 0) {
          serverVotes[userSelectedOption] = 1;
        }
      }
    }

    // Calculate total votes from the votes array
    const totalVotes = serverVotes.reduce((sum, v) => sum + v, 0);
    const participantsVoted = results?.participantsVoted > 0 ? results.participantsVoted : (userHasVoted ? 1 : 0);

    return {
      options: results?.options || options || [],
      votes: serverVotes,
      totalVotes: Math.max(totalVotes, results?.totalVotes || 0),
      participantsVoted: Math.max(participantsVoted, userHasVoted ? 1 : 0),
      totalParticipants: results?.totalParticipants || totalParticipants,
    };
  };

  const resultsData = calculateResultsData();

  // Activity Ended but Not Responded View
  if ((isActivityEnded || isActivityCompleted) && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">POLL</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this poll activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults) {
    const responseRate = resultsData.totalParticipants > 0
      ? Math.round((resultsData.participantsVoted / resultsData.totalParticipants) * 100)
      : 0;

    const activityHasEnded = isActivityEnded || isActivityCompleted;

    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button
                    onClick={onBackToList}
                    className="text-neutral-600 hover:text-neutral-900 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {activityHasEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">POLL</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success/Status Message */}
          {userResponse || selectedOption !== null || selectedOptions.length > 0 ? (
            <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-green-800">Vote Submitted!</h2>
                  <p className="text-sm sm:text-base text-green-700">
                    {activityHasEnded
                      ? 'This activity has ended. Your vote has been recorded.'
                      : 'Thank you for participating'}
                  </p>
                </div>
              </div>
            </div>
          ) : activityHasEnded ? (
            <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-neutral-100 border border-neutral-300 rounded-xl">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-neutral-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-xl font-bold text-neutral-700">Activity Ended</h2>
                  <p className="text-sm sm:text-base text-neutral-600">This poll has been closed by the trainer</p>
                </div>
              </div>
            </div>
          ) : null}

          {/* Results */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-4 sm:mb-6">{activity.title}</h3>

            {(resultsData.options || []).map((option, index) => {
              const votes = (resultsData.votes || [])[index] || 0;
              const percentage = resultsData.totalVotes > 0 ? Math.round((votes / resultsData.totalVotes) * 100) : 0;
              const isUserChoice = isPollMultiple
                ? selectedOptions.includes(index)
                : selectedOption === index;

              return (
                <div key={index} className="relative">
                  <div className={`p-3 sm:p-4 rounded-xl border-2 ${isUserChoice ? 'border-primary-500 bg-primary-50' : 'border-neutral-200'}`}>
                    <div className="flex items-start sm:items-center justify-between mb-2 gap-2">
                      <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-wrap">
                        <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg border-2 border-neutral-300 flex items-center justify-center text-xs sm:text-sm font-bold text-neutral-600 flex-shrink-0">
                          {optionLetters[index]}
                        </span>
                        <span className="text-sm sm:text-base font-medium text-neutral-900">{option}</span>
                        {isUserChoice && (
                          <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full whitespace-nowrap">
                            Your vote
                          </span>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm font-semibold text-neutral-600 flex-shrink-0">{percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-500 ${isUserChoice ? 'bg-primary-500' : 'bg-neutral-400'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Participation Stats */}
            <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-neutral-50 rounded-xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-2 text-xs sm:text-sm">
                <span className="text-neutral-600">{resultsData.participantsVoted} of {resultsData.totalParticipants} participants voted</span>
                <span className="font-semibold text-primary-600">{responseRate}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input View - Clean Design
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button
                  onClick={onBackToList}
                  className="text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">POLL</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Timer Section - Mobile (shown at top on mobile, hidden when timer disabled) */}
          {timerEnabled && (
          <div className="lg:hidden order-first">
            <div className="flex flex-row items-center gap-4 mb-6 p-4 bg-neutral-50 rounded-xl">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#ff8a00"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                </div>
              </div>
              <div className="flex-1">
                <span className="text-sm text-neutral-500">Time Remaining</span>
                <div className="text-xs text-neutral-400 mt-1">{isPollMultiple ? 'Multiple Choice' : 'Single Choice'}</div>
              </div>
            </div>
          </div>
          )}

          {/* Left Side - Question & Options */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Question Title */}
            <h1 className="text-xl lg:text-3xl font-bold text-neutral-900 mb-4 lg:mb-8 leading-tight">
              {activity.title}
            </h1>

            {activity.question && activity.question !== activity.title && (
              <p className="text-base lg:text-lg text-neutral-600 mb-4 lg:mb-6">{activity.question}</p>
            )}

            {isPollMultiple && (
              <div className="mb-6 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg inline-flex items-center gap-2">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium text-blue-800">Select multiple options</span>
              </div>
            )}

            {/* Options */}
            <div className="space-y-3">
              {options.map((option, index) => {
                const isSelected = isPollMultiple
                  ? selectedOptions.includes(index)
                  : selectedOption === index;

                return (
                  <button
                    key={index}
                    onClick={() => (isPollMultiple ? toggleMultipleOption(index) : setSelectedOption(index))}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all flex items-center gap-4 ${
                      isSelected
                        ? 'border-primary-500 bg-white shadow-sm'
                        : 'border-neutral-200 hover:border-neutral-300 bg-white'
                    }`}
                  >
                    {/* Letter Label */}
                    <span className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isSelected
                        ? 'border-primary-500 text-primary-600 bg-primary-50'
                        : 'border-neutral-300 text-neutral-500'
                    }`}>
                      {optionLetters[index]}
                    </span>

                    {/* Option Text */}
                    <span className="flex-1 font-medium text-neutral-900">{option}</span>

                    {/* Checkmark */}
                    {isSelected && (
                      <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <button
                onClick={handleSubmit}
                disabled={isPollMultiple ? selectedOptions.length === 0 : selectedOption === null}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                Submit Answer
              </button>
            </div>
          </div>

          {/* Right Side - Timer (Desktop only, hidden when timer disabled) */}
          <div className="hidden lg:block w-64 flex-shrink-0 order-3 lg:order-2">
            <div className="sticky top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-col items-center mb-8">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 mt-2">Time Remaining</span>
              </div>
              )}

              {/* Poll Info */}
              <div className="bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Poll Info
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-neutral-700">{isPollMultiple ? 'Multiple Choice' : 'Single Choice'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Options:</span>
                    <span className="font-medium text-neutral-700">{options.length}</span>
                  </div>
                </div>
              </div>

              {/* Anonymous Notice */}
              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Your vote is anonymous</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PollActivity;
